import { Controller } from "@hotwired/stimulus";
import { get, post } from "@rails/request.js";
import { setValueOrEmpty } from "../lib/empty_value";
import { setButtonLoading } from "../lib/button_loading";
import { syncGateReason } from "../lib/gate_reason";
import { showAcknowledgementModal, showAlertModal, showConfirmModal } from "../lib/custom_confirm";
import { redirectToLoginForExpiredSession, sessionExpiredResponse } from "../lib/session_expiry";
import { mountGlModalShell } from "../lib/gl_modal_shell";
import { mountStateLeadFlow } from "../lib/state_lead_flow";
import { fetchFillForecast, fillForecastQuery, renderFillForecast } from "../lib/fill_forecast";
import { deliveryWindowError, timeInputValue } from "../lib/delivery_window";
import {
  confirmStatesIfUnlicensed,
  renderUnlicensedStatesNotice,
  SELF_SUBJECT,
} from "../lib/unlicensed_states_notice";

// #12797: what the Thank-You step asks for when a self-buyer completes it with
// no photo and no skip — the two ways forward, never a refusal to buy.
const PHOTO_STEP_MESSAGE =
  "Add a photo of yourself, or choose \u201cI'll add my photo later\u201d, to complete this section.";

// Drives the redesigned single-screen realtime order screen (#8790) in BOTH of
// its shapes (#13905): the standalone page, where this controller rides the
// standard_page_shell root, and the GOAT Store order dialog, where the SAME
// content renders inside the shared adapter's gl_modal and this controller
// rides the dialog root (so the footer's one order primary is an addToCart
// target of this instance). `hosted` tells the two apart where it matters:
// a recipient change reloads the dialog fragment instead of the host page, and
// the rail's own primary is hidden so the dialog carries exactly one. Every DOM
// lookup is scoped to this.element — a dialog opened over another screen must
// never read or write that screen's fields (revenue-critical review, finding 5)
// — and disconnect() invalidates every in-flight request, so a response landing
// after the dialog closed can neither paint nor navigate (finding 5).
//  1. Lead-type picker — toggles which per-type detail panel is visible.
//  2. Quality tiers — toggles which type's tier group is visible + tier selection.
//  3. Order Options — state/day chips + max-per-day stepper.
//  4. Order Summary rail — quantity, live totals, bump, subscription, gating.
// MONEY IS SERVER-AUTHORITATIVE: every price shown comes from the /pricing
// endpoint (LeadPricingCalculator); the client only formats + multiplies the
// server unit price for a live preview. The charged amount is re-computed and
// snapshotted server-side at create.
// Steps whose card stays open once satisfied (owner 2026-09-15): the type and
// tier pickers, where the agent may still want to change the selection.
const PICKER_STEP_KEYS = new Set(["type", "quality"]);

// BUTTON-4: a held primary always names its reason. While the order is being
// placed (confirm prompts, then the POST) this is the reason; otherwise it is
// the cart hint's own sentence, so the hint and the tooltip never disagree.
const PLACING_ORDER_REASON = "Placing this order now";
const INCOMPLETE_ORDER_REASON = "Complete all required sections first";

// #13517: an order that will deliver on fewer than this many days gets one
// confirmation at submit that names the days: buyers can miss a one-chip
// selection before Add to Cart; three or more days is read as a deliberate
// schedule.
const FEW_DELIVERY_DAYS_THRESHOLD = 3;
const DAY_NAMES = {
  MON: "Monday", TUE: "Tuesday", WED: "Wednesday", THU: "Thursday",
  FRI: "Friday", SAT: "Saturday", SUN: "Sunday",
};

// One checkout, one daily cap (#14067): mirrors LeadOrders::DailyCapSplit so the
// order form previews the exact shares the server will save. Equal shares,
// remainder to the earliest orders (main order first), never below 1 because a
// saved max_per_day of 0 means "no limit".
export function dailyCapShares(cap, orderCount) {
  const total = parseInt(cap, 10);
  const count = parseInt(orderCount, 10);
  if (!(total > 0) || !(count > 0)) return null;
  const base = Math.floor(total / count);
  const remainder = total % count;
  return Array.from({ length: count }, (_, index) => Math.max(base + (index < remainder ? 1 : 0), 1));
}

export default class extends Controller {
  static targets = [
    "tile", "detailPanel", "tiersGroup", "tier",
    "stateChip", "stateCount", "unlicensedStatesNotice", "dayChip", "dayCount", "maxPerDay",
    // Local (radius) targeting
    "geoModeChip", "geoPanel", "geoCenterZip", "geoRadius", "geoRadiusLabel",
    "geoMessage", "geoZipList", "geoActions", "geoCount", "zipRow",
    "windowStart", "windowEnd", "limitsWarning",
    // Summary rail
    "qty", "minQtyLabel", "minTierLabel", "sumLeads", "sumQuality", "perLead",
    "savingsLine", "savingsAmt", "subSaveAmt", "bumpLine", "bumpLineK", "bumpLineV",
    "bumpBox", "bumpCheckbox", "bumpQty", "bumpPerLead", "bumpBoxTotal",
    "dailyCapSplitSection", "dailyCapSplitCheckbox", "dailyCapSplitHint",
    "totalLabel", "totalVal", "perLeadNote", "subToggle", "secureSub",
    "progressLabel", "progressFill", "checkItem", "cartHint", "cartError", "addToCart",
    "pickBadge", "qualityBadge", "detailCard",
    "fieldsPanel", "fieldsTitle", "fieldsBody",
    "stateFlowButton", "stateFlowPanel", "stateFlowBody", "fillForecast",
    "useProfilePhotoFlag", "photoPreview", "photoHint",
    "photoNudge", "skipPhotoWrap", "skipPhotoNote", "saveProfilePhotoWrap", "saveProfilePhotoBox",
    "pricingWarning", "stepSection",
    "thankYouField", "saveDefaultsWrap", "saveDefaultsBox",
    "ghlStatus", "ghlToggle",
  ];
  static values = {
    // #13905: true inside the GOAT Store order dialog (see the header comment).
    hosted: Boolean,
    // GOAT Store Buy switch (#14937): non-blank = the order primary stays
    // disabled with this server-painted reason however complete the form is
    // (the create endpoint refuses the order with the same sentence).
    purchaseBlockedReason: String,
    selectedClass: String,
    profileStates: Array,
    licensedStates: Array,
    profileDays: Array,
    homeState: String,
    todayDay: String,
    pricingUrl: String,
    minLeads: Object,
    maxQty: Number,
    createUrl: String,
    cartUrl: String,
    loadFieldsUrl: String,
    stateLeadFlowUrl: String,
    // Slow-fill warning (#16834): GET /realtime_lead_orders/fill_forecast.
    fillForecastUrl: String,
    zipsInRadiusUrl: String,
    // Local (Radius) availability (#14101): the lead classes with a purchasable
    // radius package (LeadPricingPackage.radius_purchasable_lead_types) and the
    // buyer-facing message the chip shows for every other class. Server-rendered
    // with the page so a type pick needs no round-trip; /pricing re-answers it
    // authoritatively.
    radiusLeadClasses: Array,
    radiusUnavailableMessage: String,
    iulUrl: String,
    excludeState: String,
    truckerClass: String,
    // Switch Subscription (#13430): the live weekly subscription this order
    // replaces, already re-resolved server-side against the buyer's own orders.
    // Sent back untouched; the create action re-resolves it again.
    switchFromId: String,
    // Buying-for (#9485): the server-resolved linked agent this order is
    // bought FOR ("" = plain self-buy). Present only when the page was
    // rendered buying for an agent; echoed back as the top-level agency_agent_id
    // create param (the server re-resolves it — never trusted as-is).
    agencyAgentId: String,
  };

  connect() {
    this._closed = false;
    // One order primary per screen: hosted, the dialog footer's button is the
    // primary (MODAL-2), so the rail's own button — the same addToCart target,
    // rendered by the shared summary partial — is taken out of the flow.
    if (this.hostedValue) this.hideRailPrimary();
    // Owner (2026-07-23): nothing is auto-selected on a bare /order — the agent
    // must click a type (Step 1). A deep-linked ?type= (legacy per-type
    // redirects) arrives in selectedClassValue and counts as the agent's own
    // Step 1 choice — and, like a tile click, adopts that type's default tier
    // (owner 2026-09-15, see syncQualityStep).
    if (this.hasQualityBadgeTarget) this.qualityStepLabel = this.qualityBadgeTarget.textContent.trim();
    if (this.selectedClassValue) this.completeTypeStep();
    this.render();
    this.syncQualityStep();
    this.updateStateCount();
    this.updateDaySummary();
    this.syncMinForSelectedTier();
    this.refreshSummary();
    this.recomputeGate();
    this.initAccordion();
  }

  // The dialog is removed from the document on close (adapter §5.5) and the
  // page can be Turbo-cached: cancel the debounced pricing refresh and
  // invalidate every request token, so a pricing / ZIP / fields / create
  // response that lands afterwards is dropped by its own token guard and
  // never paints into — or navigates — a later instance.
  disconnect() {
    this._closed = true;
    clearTimeout(this._summaryTimer);
    this._summaryTimer = null;
    clearTimeout(this._forecastTimer);
    this._forecastTimer = null;
    this._forecastToken = (this._forecastToken || 0) + 1;
    this._pricingToken = (this._pricingToken || 0) + 1;
    this._zipToken = (this._zipToken || 0) + 1;
    this._fieldsToken = (this._fieldsToken || 0) + 1;
    // The element may already be leaving the document: tear the modal down
    // rather than animate a hide on it.
    this.closeStateLeadFlow({ destroy: true });
  }

  // Hosted: every addToCart target outside the dialog footer is the rail's copy.
  hideRailPrimary() {
    this.addToCartTargets.forEach((button) => {
      if (!button.closest(".gl-modal-footer")) button.hidden = true;
    });
  }

  // The order primaries this instance drives: the rail button on the page; the
  // footer button (plus the hidden rail copy) in the dialog. Always all of them,
  // so the gate can never enable a button the buyer cannot see.
  setPrimaryDisabled(disabled) {
    const blocked = this.purchaseBlocked();
    const reason = this.primaryHeldReason(blocked);
    this.addToCartTargets.forEach((button) => {
      syncGateReason(button, { ready: !(disabled || blocked), reason });
    });
  }

  primaryHeldReason(blocked) {
    if (blocked) return this.purchaseBlockedReasonValue.trim();
    if (this._submitting) return PLACING_ORDER_REASON;
    const hint = this.hasCartHintTarget ? this.cartHintTarget.textContent.trim().replace(/\.$/, "") : "";
    return hint || INCOMPLETE_ORDER_REASON;
  }

  // BUTTON-8: the POST keeps the primary's label with a spinner and aria-busy.
  setPrimaryBusy(busy) {
    this.addToCartTargets.forEach((button) => setButtonLoading(button, busy));
  }

  purchaseBlocked() {
    return this.purchaseBlockedReasonValue.trim() !== "";
  }

  primaryDisabled() {
    return this.addToCartTargets.length > 0 && this.addToCartTargets.every((button) => button.disabled);
  }

  // --- Sequential accordion (owner 2026-07-23, amended 2026-09-15) ---
  // Each step is LOCKED until every prior step has a selection. "Satisfied"
  // per step: type = a lead type picked; quality = a tier picked;
  // options/thank-you/integrations = the "Mark this section as completed" box.
  //
  // Collapse rules differ by step kind:
  // - Steps 1-2 (the pickers) STAY OPEN once satisfied — the agent may want to
  //   change the type or tier, so completing them never closes the card
  //   (owner 2026-09-15; supersedes the 2026-07-23 "satisfying a step
  //   collapses it"). The header still toggles them by hand.
  // - Steps 3-5 (the form sections) collapse on completion and open the next.
  // Satisfying any step opens the following steps up to and including the
  // first unsatisfied one, so a type pick that also adopts a default tier
  // opens Step 2 (satisfied) and Step 3 (the next thing to do) together.

  stepSatisfied(el) {
    const key = el.dataset.rloSectionKey;
    if (key === "type") return this.typePicked === true;
    if (key === "quality") return this.tierPicked;
    return el.classList.contains("is-completed");
  }

  // Lock/collapse are driven by toggling classes directly on the section
  // elements (not via each rlo-section child controller) so the initial locked
  // state is correct regardless of Stimulus connect order. The child controller
  // only needs to read the is-locked class to no-op its own header toggle.
  lockStep(el) {
    el.classList.add("is-locked", "is-collapsed");
    el.setAttribute("aria-disabled", "true");
  }

  unlockStep(el) {
    el.classList.remove("is-locked");
    el.removeAttribute("aria-disabled");
  }

  // Recompute lock state for every step: step i is unlocked only when all steps
  // before it are satisfied. Locked steps are force-collapsed.
  syncStepLocks() {
    const steps = this.stepSectionTargets;
    const satisfied = steps.map((el) => this.stepSatisfied(el));
    steps.forEach((el, i) => {
      const unlocked = i === 0 || satisfied.slice(0, i).every(Boolean);
      if (unlocked) this.unlockStep(el);
      else this.lockStep(el);
    });
    return satisfied;
  }

  // On load: lock everything not yet reachable, keep satisfied picker steps
  // open, collapse the rest, then open the first unsatisfied unlocked step
  // (Step 1 on a bare /order; Steps 1-3 when arriving deep-linked via ?type=
  // with a default tier adopted).
  initAccordion() {
    const steps = this.stepSectionTargets;
    if (steps.length === 0) return;
    const satisfied = this.syncStepLocks();
    steps.forEach((el, i) => {
      const staysOpen = this.pickerStep(el) && satisfied[i];
      el.classList.toggle("is-collapsed", !staysOpen);
    });
    const currentIdx = satisfied.findIndex(
      (isSatisfied, i) => !isSatisfied && (i === 0 || satisfied.slice(0, i).every(Boolean)),
    );
    if (currentIdx >= 0) steps[currentIdx].classList.remove("is-collapsed");
  }

  pickerStep(el) {
    return PICKER_STEP_KEYS.has(el.dataset.rloSectionKey);
  }

  // A step (identified by key) just became satisfied: a form section collapses,
  // a picker step stays open, and the steps after it open through the first
  // unsatisfied one. `justSatisfied` guards against moving the accordion when
  // the agent is merely changing an already-made selection.
  advanceAccordion(key, justSatisfied) {
    const satisfied = this.syncStepLocks();
    if (!justSatisfied) return;
    const steps = this.stepSectionTargets;
    const idx = steps.findIndex((el) => el.dataset.rloSectionKey === key);
    if (idx < 0) return;
    if (!this.pickerStep(steps[idx])) steps[idx].classList.add("is-collapsed");
    for (let j = idx + 1; j < steps.length; j += 1) {
      if (steps[j].classList.contains("is-locked")) break;
      steps[j].classList.remove("is-collapsed");
      if (!satisfied[j]) break;
    }
  }

  // --- Buying for a linked agent (#9485) ---

  buyingFor() {
    return this.hasAgencyAgentIdValue && this.agencyAgentIdValue !== "";
  }

  // Overridable seam for tests; production navigates the browser.
  navigateTo(url) {
    window.location.href = url;
  }

  // "Save these as my profile defaults" (#12306) renders hidden when every
  // pre-filled value already IS the saved profile value; the first edit to any
  // thank-you field means saving would now change something, so reveal it.
  // For a BLANK profile (data-rlo-check-on-edit) the first edit also checks the
  // box: the agent is typing the values ticket #2252 wanted remembered. Once
  // only, and never after the agent has touched the box themselves — a manual
  // toggle (before or after that first edit) is a decision we do not overrule.
  thankYouFieldEdited() {
    if (!this.hasSaveDefaultsWrapTarget) return;
    const wrap = this.saveDefaultsWrapTarget;
    wrap.hidden = false;
    if (this.saveDefaultsTouched || wrap.dataset.rloCheckOnEdit !== "true") return;
    this.saveDefaultsTouched = true;
    if (this.hasSaveDefaultsBoxTarget) this.saveDefaultsBoxTarget.checked = true;
  }

  // change-> on the save box: the agent has made an explicit choice.
  saveDefaultsToggled() {
    this.saveDefaultsTouched = true;
  }

  // GHL row (#12307): the profile default renders as a status line (badge +
  // "Change for this order") over a [hidden] checked checkbox. The checkbox is
  // the ONLY ghl_enabled control — buildOrderFormData reads it via
  // namedChecked, so the status is presentation only. Reveal swaps them and
  // moves focus to the checkbox, since the button the agent pressed disappears.
  revealGhlToggle() {
    this.ghlStatusTargets.forEach((el) => { el.hidden = true; });
    if (!this.hasGhlToggleTarget) return;
    this.ghlToggleTarget.hidden = false;
    this.ghlToggleTarget.querySelector('input[type="checkbox"]')?.focus();
  }

  // --- Order Options: states / delivery days / max per day ---

  toggleState(event) {
    event.currentTarget.classList.toggle("is-on");
    this.updateStateCount();
  }

  toggleChip(event) {
    event.currentTarget.classList.toggle("is-on");
    this.daysClearedToFloor = false;
    this.updateDaySummary();
  }

  statesSelectAll() {
    const exclude = this.hasExcludeStateValue ? this.excludeStateValue : null;
    this.stateChipTargets.forEach((chip) => {
      if (exclude && chip.dataset.state === exclude) return; // e.g. NY excluded from bulk select
      chip.classList.add("is-on");
    });
    this.updateStateCount();
  }

  // Clear never empties a row (owner rule 2026-09-01): states keep the
  // buyer's own home state, days keep today — both server-computed for the
  // prefill user (the agent when buying-for) and passed as values.
  statesClear() {
    this.clearChipsToFloor(this.stateChipTargets, "state", this.homeStateValue);
    this.updateStateCount();
  }

  statesFromProfile() {
    const profile = this.profileStatesValue || [];
    this.stateChipTargets.forEach((chip) =>
      chip.classList.toggle("is-on", profile.includes(chip.dataset.state)),
    );
    this.updateStateCount();
  }

  // ---- Local (radius) targeting -------------------------------------------
  // Statewide is the default and leaves the existing flow untouched. In local
  // mode the buyer sets a center ZIP + radius, and the server returns the ZIPs
  // in range with a 90-day supply count each so an area that cannot fill is
  // visible before paying. The checked list is submitted, but the server
  // re-resolves it (LeadOrders::GeoParams) — it can narrow, never widen.

  setGeoMode(event) {
    this.applyGeoMode(event.currentTarget.dataset.geoMode);
  }

  // `reprice: false` only when the caller reprices right after (a type pick
  // runs render() then refreshSummary()); a buyer's own click always reprices.
  applyGeoMode(mode, { reprice = true } = {}) {
    // Defensive twin of the disabled chip: a radius request for a type with no
    // radius package is never applied, whatever dispatched it.
    if (mode === "radius" && !this.radiusAvailable()) return;
    this.geoModeChipTargets.forEach((chip) =>
      chip.classList.toggle("is-on", chip.dataset.geoMode === mode),
    );
    if (this.hasGeoPanelTarget) this.geoPanelTarget.hidden = mode !== "radius";
    // Leaving Local clears the resolved list, so returning requires a fresh
    // Find ZIP Codes rather than silently reusing a stale one — and a lookup
    // still in flight is dropped, so its late answer cannot repaint the
    // hidden panel (#14273 item 6).
    if (mode !== "radius") this.invalidateZips();
    this.updateGeoCount();
    // A local order prices off its own package, and refreshSummary is the only
    // path that sends geo_mode to /pricing. Without this the buyer switches
    // mode and keeps seeing the OTHER mode's price until they happen to touch
    // quantity or quality — the exact display/charge disagreement this
    // controller's money-is-server-authoritative note says cannot happen.
    if (reprice) this.refreshSummary();
  }

  // Whether Local (Radius) can be bought for the selected type (#14101): the
  // page-rendered list from LeadPricingPackage.radius_purchasable_lead_types.
  // Never for an Agency Lead Order (#19074): it is statewide, in the agent's
  // licensed states (the server's radiusUnavailableMessage says so).
  radiusAvailable() {
    if (this.buyingFor()) return false;
    return Boolean(this.selectedClassValue) && this.radiusLeadClassesValue.includes(this.selectedClassValue);
  }

  // Runs on every type pick (render). The Local (Radius) chip is DISABLED with
  // its reason for a type with no radius package — visible, never hidden — and
  // carries the A11Y-1 pair the server paints for the deep-linked type
  // (gl_disabled_reason_attrs: the reason as gl-tooltip, "<label> — <reason>"
  // as the accessible name); enabling it withdraws exactly that pair. A buyer
  // already in Local who switches to such a type is returned to Statewide, so
  // the summary never quotes a mode the order cannot be bought in.
  syncGeoModeAvailability() {
    const chip = this.geoModeChipTargets.find((c) => c.dataset.geoMode === "radius");
    if (!chip) return;
    const available = this.radiusAvailable();
    chip.disabled = !available;
    if (available) {
      chip.removeAttribute("data-gl-tooltip");
      chip.removeAttribute("aria-label");
    } else {
      const message = this.radiusUnavailableMessageValue;
      if (message) {
        chip.setAttribute("data-gl-tooltip", message);
        chip.setAttribute("aria-label", `${chip.textContent.trim()} — ${message}`);
      }
      if (this.geoMode() === "radius") this.applyGeoMode("state", { reprice: false });
    }
  }

  geoMode() {
    const on = this.geoModeChipTargets.find((chip) => chip.classList.contains("is-on"));
    return on?.dataset.geoMode || "state";
  }

  geoRadiusChanged() {
    if (this.hasGeoRadiusLabelTarget) {
      this.geoRadiusLabelTarget.textContent = `${this.geoRadiusTarget.value} mi`;
    }
    this.invalidateZips();
  }

  geoCenterZipChanged() {
    this.invalidateZips();
  }

  // A resolved ZIP list belongs to ONE search — this lead type, this centre
  // ZIP, this radius (#14273 item 6). Any of them changing retires it: the
  // rows, the supply summary and the count go, a lookup still in flight is
  // dropped by its token, and the section cannot complete until Find ZIP
  // Codes runs again. Before this a Veteran order could post FEX's geo_zips[]
  // under FEX's supply numbers, and a widened radius never widened the order.
  invalidateZips() {
    this._zipToken = (this._zipToken || 0) + 1;
    this.renderZips([]);
    this.showGeoMessage("", false);
    this.reopenLocalOptionsWithoutZips();
  }

  // Retiring the list must also retire what vouched for it: an Order Options
  // section already marked Completed in Local mode would otherwise stay
  // Completed over an empty list, keep Add to Cart enabled, and fail only at
  // final submit (LeadOrders::GeoParams: "Select at least one ZIP code for a
  // local order."). Reopening it runs the gate, so Add to Cart disables until
  // Find ZIP Codes runs and the section is completed again. Only while the
  // order stays Local: a type with no radius package is returned to Statewide
  // by render(), where the completed section is still valid.
  reopenLocalOptionsWithoutZips() {
    if (this.geoMode() !== "radius" || !this.radiusAvailable()) return;
    if (this.selectedZips().length > 0) return;
    const section = this.element.querySelector('.rlo-section.is-completed[data-rlo-section-key="options"]');
    if (section) this.revertSectionCompletion(section);
  }

  async loadZips() {
    const centerZip = (this.geoCenterZipTarget.value || "").trim();
    if (!/^\d{5}$/.test(centerZip)) {
      this.showGeoMessage("Enter a 5-digit ZIP code to center the search.", true);
      return;
    }

    this.showGeoMessage("Finding ZIP codes…", false);
    // Guard against an out-of-order response overwriting a newer search.
    const token = (this._zipToken = (this._zipToken || 0) + 1);

    try {
      const query = {
        center_zip: centerZip,
        radius_miles: String(this.geoRadiusTarget.value),
        lead_class: this.selectedClassValue,
      };
      // Buying for an agent, licensing resolves against the RECEIVING agent,
      // not the payer; this request does not inherit the page's id, so it is
      // sent. Unreached today: radiusAvailable() keeps Local (Radius) off for
      // an Agency Lead Order, which is statewide (#19074).
      if (this.buyingFor()) query.agency_agent_id = this.agencyAgentIdValue;

      const response = await get(this.zipsInRadiusUrlValue, {
        query,
        responseKind: "json",
      });
      if (token !== this._zipToken) return;

      const data = await response.json;
      // Re-check after the body parse too — a newer search (or disconnect) can
      // supersede DURING it, and the header check above cannot see that
      // (#14097 success-body gap). Guards renderZips + showGeoMessage below.
      if (token !== this._zipToken) return;
      if (!response.ok) {
        this.renderZips([]);
        this.showGeoMessage(data?.error || "Couldn't look up that area — please try again.", true);
        return;
      }
      this.renderZips(data.zips || []);
      this.showGeoMessage(this.geoSummaryText(data), false);
    } catch {
      if (token !== this._zipToken) return;
      this.renderZips([]);
      this.showGeoMessage("Couldn't look up that area — please try again.", true);
    }
  }

  geoSummaryText(data) {
    const parts = [
      `${data.zips.length} ZIP codes · ${data.total_recent_leads} leads in the last 90 days`,
    ];
    if (data.excluded_states?.length) {
      parts.push(
        `Excluded ${data.excluded_states.join(", ")} — you are not licensed there.`,
      );
    }
    if (data.total_recent_leads === 0) {
      parts.push("This area produced no leads recently and may not fill.");
    }
    return parts.join(" ");
  }

  renderZips(zips) {
    if (!this.hasGeoZipListTarget) return;
    this.geoZipListTarget.replaceChildren(...zips.map((z) => this.buildZipRow(z)));
    if (this.hasGeoActionsTarget) this.geoActionsTarget.hidden = zips.length === 0;
    this.updateGeoCount();
  }

  // Built with DOM APIs rather than an HTML string so city/county values from
  // the response can never be interpreted as markup. Each row is a canonical
  // .gl-form-check (FORM-1): box + label anatomy from the shared owner, the
  // rlo-local__* classes carry layout only.
  buildZipRow(zip) {
    const label = document.createElement("label");
    label.className = "gl-form-check rlo-local__zip";

    const box = document.createElement("input");
    box.type = "checkbox";
    box.className = "gl-form-check-input";
    box.checked = true;
    box.dataset.zip = zip.zip;
    box.dataset.realtimeOrderTarget = "zipRow";
    box.dataset.action = "realtime-order#updateGeoCount";

    const name = document.createElement("span");
    name.className = "gl-form-check-label rlo-local__zip-name";
    name.textContent = `${zip.zip} · ${zip.city || ""}`;

    const meta = document.createElement("span");
    meta.className = "rlo-local__zip-meta";
    meta.textContent = `${zip.distance_miles} mi · ${zip.recent_leads} leads/90d`;

    label.append(box, name, meta);
    return label;
  }

  zipsSelectAll() {
    this.zipRowTargets.forEach((box) => { box.checked = true; });
    this.updateGeoCount();
  }

  zipsClear() {
    this.zipRowTargets.forEach((box) => { box.checked = false; });
    this.updateGeoCount();
  }

  selectedZips() {
    return this.zipRowTargets.filter((box) => box.checked).map((box) => box.dataset.zip);
  }

  updateGeoCount() {
    if (!this.hasGeoCountTarget) return;
    this.geoCountTarget.textContent =
      this.geoMode() === "radius" ? `${this.selectedZips().length} ZIP codes` : "Statewide";
  }

  showGeoMessage(text, isError) {
    if (!this.hasGeoMessageTarget) return;
    this.geoMessageTarget.textContent = text;
    this.geoMessageTarget.classList.toggle("is-error", Boolean(isError));
  }

  daysSelectAll() {
    this.dayChipTargets.forEach((chip) => chip.classList.add("is-on"));
    this.daysClearedToFloor = false;
    this.updateDaySummary();
  }

  // Clear keeps today's chip (the floor) so the row is never empty — but a row
  // holding ONLY that floor is a cleared row, not a chosen schedule (#13740).
  // The flag makes the Order Options gate refuse completion
  // until the buyer picks days; any chip change clears it.
  daysClear() {
    this.clearChipsToFloor(this.dayChipTargets, "day", this.todayDayValue);
    this.daysClearedToFloor = Boolean(this.todayDayValue);
    this.updateDaySummary();
  }

  // Turns every chip off except the floor (kept on when it exists in the row).
  // An empty floor clears everything — the section gate then refuses completion.
  clearChipsToFloor(chips, dataKey, floor) {
    chips.forEach((chip) =>
      chip.classList.toggle("is-on", Boolean(floor) && chip.dataset[dataKey] === floor),
    );
  }

  // Profile default = User#default_days_per_week (the prefill user's, so the
  // agent's when buying-for).
  daysFromProfile() {
    const profile = this.profileDaysValue || [];
    this.dayChipTargets.forEach((chip) =>
      chip.classList.toggle("is-on", profile.includes(chip.dataset.day)),
    );
    this.daysClearedToFloor = false;
    this.updateDaySummary();
  }

  // The profile's default days, in chip (week) order, when they make a schedule
  // worth offering from the one-or-two-day prompt: three or more days.
  profileScheduleDays() {
    const profile = this.profileDaysValue || [];
    const days = this.dayChipTargets.map((chip) => chip.dataset.day).filter((day) => profile.includes(day));
    return days.length >= FEW_DELIVERY_DAYS_THRESHOLD ? days : [];
  }

  // Every chip mutation funnels through here (toggleState / statesSelectAll /
  // statesClear / statesFromProfile / connect), so it is the one place the
  // unlicensed-state advisory and an open State Lead Flow's Selected badges
  // (#16096) need wiring. Both sit ABOVE the stateCount guard so a page
  // without that label still gets them.
  updateStateCount() {
    this.refreshUnlicensedStatesNotice();
    this._stateFlow?.setSelectedStates(this.selectedStates());
    this.scheduleFillForecast();

    if (!this.hasStateCountTarget) return;
    const count = this.stateChipTargets.filter((chip) =>
      chip.classList.contains("is-on"),
    ).length;
    this.stateCountTarget.textContent = `${count} selected`;
  }

  // --- Unlicensed-state advisory ---

  // licensedStatesValue is the FULL licensed list of order_prefill_user — the
  // RECIPIENT agent on a buy-for. Not profileStatesValue: that pre-fill list
  // drops NY (never copied onto an order), so reading it here told an
  // NY-licensed agent that a hand-picked NY was unlicensed (#16475).
  get unlicensedStatesContext() {
    const notice = this.hasUnlicensedStatesNoticeTarget
      ? this.unlicensedStatesNoticeTarget
      : null;

    return {
      licensed: this.licensedStatesValue || [],
      subject: notice?.dataset.subject || SELF_SUBJECT,
      profileUrl: notice?.dataset.profileUrl || null,
    };
  }

  selectedStates() {
    return this.selectedChipValues(this.stateChipTargets, "state");
  }

  refreshUnlicensedStatesNotice() {
    if (!this.hasUnlicensedStatesNoticeTarget) return [];

    return renderUnlicensedStatesNotice(this.unlicensedStatesNoticeTarget, {
      selected: this.selectedStates(),
      ...this.unlicensedStatesContext,
    });
  }

  // #13517: submit-time gate for a one- or two-day schedule. Resolves true when
  // there is nothing to ask (three or more days, or zero — the Order Options
  // gate refuses an empty row and the server rejects it anyway), so callers can
  // `if (!(await ...)) return` unconditionally, like confirmStatesIfUnlicensed.
  // Named days come out in chip (week) order because selectedChipValues walks
  // the DOM.
  async confirmFewDeliveryDays() {
    const days = this.selectedChipValues(this.dayChipTargets, "day");
    if (days.length === 0 || days.length >= FEW_DELIVERY_DAYS_THRESHOLD) return true;

    const names = days.map((day) => DAY_NAMES[day] || day);
    const one = names.length === 1;
    const list = one ? names[0] : names.join(" and ");
    const offDays = 7 - names.length;

    // #13740: the buyer who wanted their usual week gets it in one click
    // instead of hunting for the chips again — offered only when the profile
    // has a real schedule (three or more days) that differs from the selection.
    const profileDays = this.profileScheduleDays();
    const alternate = profileDays.length
      ? { text: `Use my profile days (${profileDays.map((day) => day.charAt(0) + day.slice(1).toLowerCase()).join(", ")})`,
          icon: "ph ph-calendar-check" }
      : undefined;

    const outcome = await showConfirmModal(
      `Only ${one ? "one delivery day is" : "two delivery days are"} selected: ${list}. `
        + `Leads on this order will arrive on ${one ? "that day" : "those days"} only — `
        + `nothing on the other ${offDays} days. `
        + `${this.buyingFor() ? "Continue" : "Add it to the cart"} anyway?`,
      {
        variant: "warning",
        title: one ? "Only one delivery day" : "Only two delivery days",
        icon: "ph ph-calendar-blank",
        confirmText: "Yes, that's right",
        cancelText: "Go back and fix",
        alternate,
      },
    );
    if (outcome === "alternate") {
      this.daysFromProfile();
      return true;
    }
    return outcome === true;
  }

  // "N days selected" — the same count idiom as the Target States row.
  updateDaySummary() {
    this.updateLimitsWarning();
    if (!this.hasDayCountTarget) return;
    const count = this.selectedChipValues(this.dayChipTargets, "day").length;
    const noun = count === 1 ? "day" : "days";
    this.dayCountTarget.textContent = `${count} ${noun} selected`;
  }

  // #15331: a limit the buyer chooses — fewer than every day, a max per day,
  // or a Time of Day window — slows the order exactly like a pause, so the
  // Order Options section says so while any limit is set. Every day chip
  // change funnels through updateDaySummary; the cap and window inputs call
  // this directly.
  updateLimitsWarning() {
    // The same limits change the slow-fill forecast (#16834).
    this.scheduleFillForecast();
    if (!this.hasLimitsWarningTarget) return;
    const days = this.selectedChipValues(this.dayChipTargets, "day").length;
    const fewerDays = this.dayChipTargets.length > 0 && days < this.dayChipTargets.length;
    const cap = this.hasMaxPerDayTarget && String(this.maxPerDayTarget.value || "").trim() !== "";
    const timeWindow = this.hasWindowStartTarget && this.hasWindowEndTarget &&
      Boolean(this.windowStartTarget.value) && Boolean(this.windowEndTarget.value);
    this.limitsWarningTarget.classList.toggle("d-none", !(fewerDays || cap || timeWindow));
  }

  // Uploading a file overrides the profile photo; clearing it restores the profile
  // photo when the buyer has one (so use_profile_photo tracks the real intent).
  // The no-upload hint copy is server-rendered into data-rlo-default (it names
  // the agent when buying-for), so restoring reads it back instead of
  // hardcoding "your profile photo".
  handlePhotoChange(event) {
    const hasFile = (event.currentTarget.files?.length || 0) > 0;
    const profileAvailable = this.hasPhotoPreviewTarget;
    if (this.hasUseProfilePhotoFlagTarget) {
      this.useProfilePhotoFlagTarget.value = hasFile ? "0" : profileAvailable ? "1" : "0";
    }
    if (this.hasPhotoHintTarget) {
      this.photoHintTarget.textContent = hasFile
        ? "Using the uploaded photo"
        : this.photoHintTarget.dataset.rloDefault || "Using your profile photo";
    }
    // #12797: choosing a file answers the nudge (and clears an earlier skip —
    // the buyer now has a photo); clearing the only photo (a cancelled crop
    // clears the input and re-fires change) brings the nudge and the skip
    // back. A photo is encouraged, never required, so clearing it never
    // un-completes the step.
    if (hasFile) this.photoSkipped = false;
    this.refreshPhotoNudge(hasFile);
  }

  // "I'll add my photo later" (#12797, the onboarding step-6 pattern): one
  // deliberate click lets the Thank-You step complete without a photo, and the
  // nudge + button give way to the note. Never a hard stop — an agent can
  // always buy.
  skipPhoto() {
    this.photoSkipped = true;
    this.refreshPhotoNudge(false);
  }

  refreshPhotoNudge(hasFile) {
    const skipped = !hasFile && this.photoSkipped === true;
    if (this.hasPhotoNudgeTarget) this.photoNudgeTarget.hidden = hasFile || skipped;
    if (this.hasSkipPhotoWrapTarget) this.skipPhotoWrapTarget.hidden = hasFile || skipped;
    if (this.hasSkipPhotoNoteTarget) this.skipPhotoNoteTarget.hidden = !skipped;
    if (this.hasSaveProfilePhotoWrapTarget) this.saveProfilePhotoWrapTarget.hidden = !hasFile;
  }

  // Reset every field in this section back to its profile default (data-rlo-default).
  applyProfileDefaults(event) {
    this.resetSectionDefaults(event.currentTarget.closest(".rlo-section"));
  }

  // Shared by the self-buy "Apply Defaults From Profile" button and the
  // buying-for "Agent's profile" thank-you mode (which relocks to these).
  resetSectionDefaults(section) {
    if (!section) return;
    section.querySelectorAll("input[data-rlo-default]").forEach((el) => {
      if (el.type === "checkbox") {
        el.checked = el.dataset.rloDefault === "true";
      } else {
        el.value = el.dataset.rloDefault;
      }
    });
    this.resetSaveDefaultsRow(section);
    this.syncTitleSuggestions();
  }

  // The values are back to what the page rendered, so the "save as my profile
  // defaults" row (revealed by thankYouFieldEdited) goes back to its rendered
  // visibility too — hidden again when nothing would change (#12306). The box
  // itself carries data-rlo-default, so the loop above already reset it; the
  // check-on-first-edit re-arms because this is a fresh start.
  resetSaveDefaultsRow(section) {
    if (!this.hasSaveDefaultsWrapTarget) return;
    const wrap = this.saveDefaultsWrapTarget;
    if (!section.contains(wrap)) return;
    wrap.hidden = wrap.dataset.rloDefaultHidden === "true";
    this.saveDefaultsTouched = false;
  }

  maxPerDayUp() {
    this.stepMaxPerDay(1);
  }

  maxPerDayDown() {
    this.stepMaxPerDay(-1);
  }

  stepMaxPerDay(delta) {
    if (!this.hasMaxPerDayTarget) return;
    const min = this.maxPerDayMinimum();
    const current = parseInt(this.maxPerDayTarget.value, 10);
    // From blank ("no limit") either arrow lands ON the minimum, the first
    // value a cap can be — not one past it (#14273 item 8).
    const next = Number.isNaN(current) ? min : current + delta;
    this.maxPerDayTarget.value = Math.max(min, next);
    this.updateDailyCapSplitHint();
    this.updateLimitsWarning();
  }

  // The floor the input advertises (LeadOrder::MINIMUM_MAX_PER_DAY, rendered
  // as its `min`); the same number the server enforces on save. Every caller
  // has the target, so the server's number is the only source.
  maxPerDayMinimum() {
    return Number(this.maxPerDayTarget.min);
  }

  // Client-side mirror of LeadOrderValidations (numericality only_integer +
  // max_per_day_meets_floor): blank means no daily limit; anything else is a
  // whole number at or above the floor. Without this "3", "7.5" and "-1"
  // completed Order Options and failed only at submit (#14273 item 8).
  maxPerDayError() {
    if (!this.hasMaxPerDayTarget) return null;
    const raw = String(this.maxPerDayTarget.value || "").trim();
    if (raw === "") return null;
    const min = this.maxPerDayMinimum();
    if (/^\d+$/.test(raw) && Number(raw) >= min) return null;
    return `Max Leads Per Day must be a whole number of at least ${min}, or left blank for no daily limit.`;
  }

  // Live preview of LeadOrders::DailyCapSplit for the Order Options checkbox
  // "Apply Daily Cap To All Orders In This Transaction" (#14067). Re-run on
  // every input that changes the answer: the cap, the bump offer, either box.
  // The texts are pinned by bump_order_spec, so a wording change is a spec change.
  updateDailyCapSplitHint() {
    if (!this.hasDailyCapSplitHintTarget) return;

    const cap = this.hasMaxPerDayTarget ? parseInt(this.maxPerDayTarget.value || "0", 10) : 0;
    const bumpSelected = this.bumpSelected();
    const apply = this.dailyCapSplitRequested();

    let text;
    if (!(cap > 0)) {
      text = "No daily max is set, so every order in this transaction is unlimited.";
    } else if (!bumpSelected) {
      text = `This transaction has one order, and it receives the full daily max of ${cap}.`;
    } else if (this.isSubscription()) {
      text = `A weekly subscription keeps your daily max of ${cap} on every order in this transaction and on every renewal.`;
    } else if (apply) {
      const [mainShare, bumpShare] = dailyCapShares(cap, 2);
      text = `Your daily max of ${cap} is divided: main order ${mainShare} a day, bump order ${bumpShare} a day, up to ${mainShare + bumpShare} leads a day in total.`;
    } else {
      text = `Each order keeps its own daily max of ${cap}: up to ${cap * 2} leads a day across the main order and the bump order.`;
    }
    this.dailyCapSplitHintTarget.textContent = text;
  }

  // The per-order cap notice and its checkbox only make sense while a second
  // order (the bump) WILL exist: the rail shows an offer AND the buyer has left
  // the bump box checked. renderSummary / invalidatePricing keep it in step.
  // Never for a weekly subscription (#14270): the split can only divide the
  // parent's own cap, and every renewal child is minted from a dup of the parent
  // — a divided parent renews on its share, or not at all below the floor of 5.
  // The server refuses the split for a weekly primary regardless; hiding the box
  // (and keeping the param off the wire, see dailyCapSplitRequested) just stops
  // the form promising a division that will not happen.
  updateDailyCapSplitVisibility() {
    if (!this.hasDailyCapSplitSectionTarget) return;
    this.dailyCapSplitSectionTarget.classList.toggle("d-none", !this.dailyCapSplitAvailable());
  }

  dailyCapSplitAvailable() {
    return this.bumpSelected() && !this.isSubscription();
  }

  dailyCapSplitRequested() {
    return this.dailyCapSplitAvailable() && this.hasDailyCapSplitCheckboxTarget &&
      this.dailyCapSplitCheckboxTarget.checked;
  }

  // Visibility + hint together. renderSummary / invalidatePricing run it after
  // pricing lands; the weekly toggle runs it synchronously (before its own
  // refreshSummary action) so the block never lingers on screen for a weekly
  // buyer while the re-price is in flight.
  syncDailyCapSplit() {
    this.updateDailyCapSplitVisibility();
    this.updateDailyCapSplitHint();
  }

  selectType(event) {
    const leadClass = event.currentTarget.dataset.leadClass;
    if (!leadClass) return;
    const firstPick = !this.typePicked;
    const tierWasPicked = this.tierPicked;
    // A Local ZIP list was resolved FOR the previous type (its supply counts,
    // its licensing) — a real switch retires it (#14273 item 6). Retired AFTER
    // the new type is set, so reopening Order Options can ask whether the NEW
    // type keeps the order Local (radiusAvailable reads selectedClassValue).
    const typeChanged = leadClass !== this.selectedClassValue;
    this.selectedClassValue = leadClass;
    if (typeChanged) this.invalidateZips();
    if (typeChanged) this.closeStateLeadFlow();
    this.completeTypeStep();
    this.render();
    this.syncQualityStep();
    this.syncMinForSelectedTier();
    this.refreshSummary();
    this.recomputeGate();
    this.advanceAccordion("type", firstPick);
    // Switching types can satisfy Step 2 on its own (the new group's default
    // tier) — or un-satisfy it (a group with nothing purchasable, which
    // syncStepLocks has just re-locked). Treat a fresh satisfaction like a tier
    // click so the form sections after it open again.
    if (!tierWasPicked && this.tierPicked) this.advanceAccordion("quality", true);
  }

  // Select a tier within its own type group (does not touch other groups).
  selectTier(event) {
    const button = event.currentTarget;
    const group = button.closest(".rlo-tiers-group");
    if (!group) return;
    const firstPick = !this.tierPicked;
    group.querySelectorAll(".rlo-tier").forEach((tier) => {
      const on = tier === button;
      tier.classList.toggle("is-selected", on);
      tier.setAttribute("aria-pressed", on ? "true" : "false");
    });
    this.syncQualityStep();
    this.syncMinForSelectedTier();
    this.refreshSummary();
    this.recomputeGate();
    this.advanceAccordion("quality", firstPick);
  }

  // Step 2 (Choose Your Lead Quality) is satisfied by the LIVE selection in the
  // active type group — never a latch. Picking a type adopts that type's default
  // tier (owner 2026-09-15): the server renders it — Premium when purchasable,
  // see RealtimeLeadOrdersController#default_tier_for — as the group's
  // is-selected tile, so Step 2 is satisfied the moment the group is active, and
  // the agent changes it by clicking another tier while the card stays open.
  // Because it is live, switching to a type whose group has nothing purchasable
  // (every tile Coming Soon, nothing selected) un-satisfies Step 2 again instead
  // of leaving a stale "Completed" pill over an empty selection.
  get tierPicked() {
    return this.selectedTier() !== null;
  }

  // Re-render the Step 2 pill from the live selection: "Completed" when the
  // active group has a tier, otherwise back to its step label ("Step 2").
  syncQualityStep() {
    // Runs on connect and after every type / tier change, once the active tier
    // group is painted — the one place the State Lead Flow button follows it.
    this.syncStateLeadFlowButton();
    if (!this.hasQualityBadgeTarget) return;
    if (this.tierPicked) {
      this.markBadgeDone(this.qualityBadgeTarget);
    } else {
      this.qualityBadgeTarget.textContent = this.qualityStepLabel || "Step 2";
      this.qualityBadgeTarget.dataset.state = "required";
    }
  }

  // Step 1 (Pick Lead Type) completes on the FIRST active choice — a tile click,
  // or landing deep-linked via ?type= — and stays complete: there is always a
  // selected type afterwards, so unlike Step 2 it cannot un-satisfy. Steps 1-2
  // feed the 5-step progress bar + checklist; Add to Cart is gated by the 3 form
  // sections plus selectedTier() (pricing needs a chosen tier).
  completeTypeStep() {
    if (this.typePicked) return;
    this.typePicked = true;
    if (this.hasPickBadgeTarget) this.markBadgeDone(this.pickBadgeTarget);
  }

  markBadgeDone(badge) {
    badge.textContent = "Completed";
    badge.dataset.state = "done";
  }

  // Single sync point for type selection: highlight the tile, show its detail
  // panel, and show its tier group; everything else off.
  render() {
    const value = this.selectedClassValue;

    if (this.hasDetailCardTarget) {
      this.detailCardTarget.style.display = value ? "" : "none";
    }

    this.tileTargets.forEach((tile) => {
      const on = tile.dataset.leadClass === value;
      tile.classList.toggle("is-selected", on);
      tile.setAttribute("aria-pressed", on ? "true" : "false");
    });

    this.detailPanelTargets.forEach((panel) => {
      panel.classList.toggle("is-active", panel.dataset.leadClass === value);
    });

    this.tiersGroupTargets.forEach((group) => {
      group.classList.toggle("is-active", group.dataset.leadClass === value);
    });

    this.syncGeoModeAvailability();
    this.syncTitleSuggestions();
  }

  // Suggested thank-you titles (#12908): the chips partial rides inside the
  // thank-you section under its own controller (title-suggestions). Hand it the
  // type being bought — it fronts that type's chip — and tell it to re-read the
  // Title input after THIS controller writes fields programmatically (profile
  // defaults, the owner's "My info" values, the profile lock), since none of
  // those fire `input`.
  syncTitleSuggestions() {
    this.element.querySelectorAll('[data-controller~="title-suggestions"]').forEach((el) => {
      el.dataset.titleSuggestionsLeadTypeValue = this.selectedClassValue || "";
      el.dispatchEvent(new CustomEvent("title-suggestions:sync"));
    });
  }

  // --- Order Summary rail ---

  // The tier selected inside the visible (active) type group, e.g. "premium".
  selectedTier() {
    const group = this.tiersGroupTargets.find((g) =>
      g.classList.contains("is-active"),
    );
    const selected = group?.querySelector(".rlo-tier.is-selected");
    return selected?.dataset.tier || null;
  }

  minForTier(tier) {
    const map = this.hasMinLeadsValue ? this.minLeadsValue : {};
    return Number(map[tier] ?? map.blended ?? 20) || 20;
  }

  syncMinForSelectedTier() {
    const min = this.minForTier(this.selectedTier());
    if (this.hasQtyTarget) {
      this.qtyTarget.min = min;
      if ((parseInt(this.qtyTarget.value, 10) || 0) < min) this.qtyTarget.value = min;
    }
    if (this.hasMinQtyLabelTarget) this.minQtyLabelTarget.textContent = String(min);
    if (this.hasMinTierLabelTarget) {
      const tier = this.selectedTier();
      this.minTierLabelTarget.textContent = tier ? this.titleize(tier) : "";
    }
  }

  qtyUp() {
    this.stepQty(5);
  }

  qtyDown() {
    this.stepQty(-5);
  }

  stepQty(delta) {
    if (!this.hasQtyTarget) return;
    const min = this.minForTier(this.selectedTier());
    const max = Number(this.maxQtyValue) || 1000;
    const current = parseInt(this.qtyTarget.value, 10);
    const base = Number.isNaN(current) ? min : current;
    this.qtyTarget.value = Math.min(max, Math.max(min, base + delta));
    this.refreshSummary();
  }

  clampQty() {
    if (!this.hasQtyTarget) return;
    const min = this.minForTier(this.selectedTier());
    const max = Number(this.maxQtyValue) || 1000;
    const value = parseInt(this.qtyTarget.value, 10) || min;
    this.qtyTarget.value = Math.min(max, Math.max(min, value));
    this.refreshSummary();
  }

  clampedQty() {
    const min = this.minForTier(this.selectedTier());
    const max = Number(this.maxQtyValue) || 1000;
    const parsed = parseInt(this.qtyTarget?.value, 10);
    if (Number.isNaN(parsed)) return min;
    return Math.min(max, Math.max(min, parsed));
  }

  // Debounced entry point for the quantity input (many keystrokes -> one fetch).
  refreshSummaryDebounced() {
    clearTimeout(this._summaryTimer);
    this._summaryTimer = setTimeout(() => this.refreshSummary(), 250);
  }

  // Fetch server-authoritative pricing for the current type/quality/quantity and
  // render the summary. Never computes a unit price — only formats + multiplies.
  // On ANY failure the summary is put into an explicit invalid state (no stale
  // money left on screen) and Add to Cart is blocked, so the displayed price can
  // never disagree with what /create would charge.
  async refreshSummary() {
    // Type, quality and quantity all re-price through here; the slow-fill
    // forecast (#16834) reads the same three.
    this.scheduleFillForecast();
    const tier = this.selectedTier();
    // #11827: the fetch does not wait for a tier — the tier TILES re-price from
    // it too (updateTierTiles), so a quantity change before any tier is chosen
    // still moves every tile. A type must be chosen, or there is nothing to price.
    if (!this.hasPricingUrlValue || !this.selectedClassValue) {
      this.invalidatePricing();
      return;
    }
    const quantity = this.clampedQty();
    // Monotonic token: ignore any response that isn't for the latest request, so
    // out-of-order responses can't render a price for a stale selection.
    const token = (this._pricingToken = (this._pricingToken || 0) + 1);
    // No tier yet → the summary stays blank silently; a failed fetch is only an
    // error the buyer should see once they have picked a tier to price.
    const failureMessage = tier ? "Couldn't load current pricing — please try again." : undefined;

    let data;
    try {
      // geo_mode so the quoted price is the package the order will actually be
      // charged against — a local order prices off its own package. quality is
      // only sent once a tier is picked, never as a blank value.
      const query = {
        lead_class: this.selectedClassValue,
        quantity: String(quantity),
        geo_mode: this.geoMode(),
      };
      if (tier) query.quality = tier;
      // Agency Lead Order (#19646): the weekly figure is the package price,
      // never the payer's own custom subscription offer — the server leaves
      // the offer out of the quote exactly when the charge leaves it out.
      if (this.buyingFor()) query.agency_agent_id = this.agencyAgentIdValue;
      const response = await get(this.pricingUrlValue, { query, responseKind: "json" });
      if (token !== this._pricingToken) return; // superseded by a newer request
      // The session ended (401, or a followed redirect to sign-in): "Couldn't
      // load current pricing" would stay forever. Go and log in instead
      // (#14273 item 7); return_to brings the buyer back to this page.
      if (sessionExpiredResponse(response)) return redirectToLoginForExpiredSession();
      if (!response.ok) {
        this.invalidatePricing(failureMessage);
        return;
      }
      data = await response.json;
      // The header check above ran before this body parse, which is itself a
      // suspension point: a newer request (or disconnect) can supersede DURING
      // it. Re-check before ANY state/toast mutation so a stale success body
      // can't repaint over the current selection (#14097 success-body gap).
      if (token !== this._pricingToken) return;
      // Admin-only: the server includes admin_pricing_warnings only for admins.
      this.showAdminPricingWarnings(data?.admin_pricing_warnings);
    } catch (error) {
      if (token !== this._pricingToken) return;
      console.error("Could not fetch pricing", error);
      this.invalidatePricing(failureMessage);
      return;
    }

    // The server's explicit verdict (#14101): nothing was priced for this
    // selection — today a Local quote for a type with no radius package — and
    // it says why. The tiles are left alone (there is no price to repaint them
    // from) and the summary shows the server's reason, not a generic one.
    if (data?.available === false) {
      this.invalidatePricing(data.message || "Pricing is unavailable for this selection.");
      return;
    }

    this.updateTierTiles(data?.prices);
    if (!tier) {
      this.invalidatePricing();
      return;
    }
    const priceRow = data?.prices?.[tier];
    // A missing or non-positive unit price is never a real $0 lead — treat as unavailable.
    if (!priceRow || priceRow.unit_price == null || Number(priceRow.unit_price) <= 0) {
      this.invalidatePricing("Pricing is unavailable for this selection.");
      return;
    }

    this.hideCartError();
    this.pricingValid = true;
    this.renderSummary(priceRow, quantity, tier);
    this.recomputeGate();
  }

  // #11827: re-price every tier tile in the active type's group from the SAME
  // /pricing response the summary renders — the retired per-type quality cards
  // did exactly this from the same payload — so a tile
  // can never quote a different per-lead price than the summary charges, and swap
  // in the server-composed tier tagline (RealtimeOrderTierTagline; the client
  // never composes that copy). Unavailable ("Coming Soon") tiles are left alone;
  // a null tagline hides the line.
  //
  // Per-tier minimums are the SERVER's concern: /pricing prices each quality at
  // the requested quantity raised to that quality's own minimum (the quantity the
  // stepper snaps to when the tile is selected), so a tile whose minimum sits
  // above the current quantity is rendered as-is — its row already quotes the
  // price and tagline the buyer would actually get by picking it.
  updateTierTiles(prices) {
    if (!prices || !this.selectedClassValue) return;
    const group = this.element.querySelector(
      `.rlo-tiers-group[data-lead-class="${this.selectedClassValue}"]`,
    );
    if (!group) return;

    for (const [quality, row] of Object.entries(prices)) {
      const tile = group.querySelector(`.rlo-tier[data-tier="${quality}"]`);
      if (!tile || tile.classList.contains("is-unavailable")) continue;

      const priceEl = tile.querySelector(".rlo-tier__price");
      if (priceEl && row?.unit_price != null && Number(row.unit_price) > 0) {
        priceEl.textContent = this.money(row.unit_price);
      }
      const taglineEl = tile.querySelector(".rlo-tier__tagline");
      if (taglineEl && row && "tier_tagline" in row) {
        taglineEl.textContent = row.tier_tagline || "";
        taglineEl.classList.toggle("d-none", !row.tier_tagline);
      }
      this.updateTierWeekly(tile, row);
    }
  }

  // #19378: the tile's "Save $X/lead · $Y/lead on a weekly plan" line is the
  // SAME weekly figure the summary charges — the row's subscription_price (the
  // price at this quantity less the weekly savings, or the buyer's custom
  // offer) — and the saving is the summary's own unit − weekly. A tile with no
  // saving left hides the line, as the server does on first paint.
  updateTierWeekly(tile, row) {
    const savingsEl = tile.querySelector(".rlo-tier__savings");
    if (!savingsEl || row?.unit_price == null || row?.subscription_price == null) return;

    const unit = Number(row.unit_price);
    const weekly = Number(row.subscription_price);
    const savings = Math.max(0, unit - weekly);
    const pillAmount = savingsEl.querySelector(".rlo-tier__pill-amount");
    if (pillAmount) pillAmount.textContent = this.money(savings);
    const weeklyAmount = savingsEl.querySelector(".rlo-tier__weekly-amount");
    if (weeklyAmount) weeklyAmount.textContent = this.money(weekly);
    savingsEl.classList.toggle("d-none", savings <= 0);
  }

  // Blank every money figure and block checkout — used whenever we don't have a
  // trustworthy server price for the CURRENT selection.
  invalidatePricing(message) {
    this.pricingValid = false;
    ["perLead", "sumLeads", "totalVal", "savingsAmt", "subSaveAmt", "bumpLineV"].forEach((t) =>
      this.setTextOrEmpty(t, null),
    );
    this.setTextOrEmpty("sumQuality", this.selectedTier() ? this.titleize(this.selectedTier()) : null);
    if (this.hasSavingsLineTarget) this.savingsLineTarget.style.display = "none";
    if (this.hasBumpBoxTarget) this.bumpBoxTarget.style.display = "none";
    if (this.hasBumpLineTarget) this.bumpLineTarget.style.display = "none";
    this.syncDailyCapSplit();
    if (message) this.showCartError(message);
    this.recomputeGate();
  }

  renderSummary(row, quantity, tier) {
    const unit = Number(row.unit_price);
    const subPrice = Number(row.subscription_price ?? unit);
    const savings = Math.max(0, unit - subPrice);
    // An Agency Lead Order never carries a bump (#19074; create refuses one
    // too), so the offer is suppressed even if /pricing sends one.
    const bump = this.buyingFor() ? null : row.bump_offer || null;
    const isSub = this.hasSubToggleTarget && this.subToggleTarget.checked;

    // Bump offer box visibility (only when the server offers one for this qty).
    // Re-default the checkbox to CHECKED whenever a bump offer (re)appears after
    // being hidden, so a qty change that brings the offer back opts the buyer in
    // again.
    if (this.hasBumpBoxTarget) {
      const wasVisible = this.bumpBoxTarget.style.display !== "none";
      this.bumpBoxTarget.style.display = bump ? "" : "none";
      if (bump && !wasVisible && this.hasBumpCheckboxTarget) this.bumpCheckboxTarget.checked = true;
    }
    const bumpOn = bump && this.hasBumpCheckboxTarget && this.bumpCheckboxTarget.checked;
    // One checkout, one daily cap (#14067): the Order Options notice + checkbox
    // follow the bump offer's visibility, the buyer's bump choice and the
    // weekly toggle (#14270).
    this.syncDailyCapSplit();
    if (bump) {
      this.setText("bumpQty", String(bump.quantity));
      this.setText("bumpPerLead", this.money(bump.price_per_lead));
      this.setText("bumpBoxTotal", this.money(bump.total_price));
    }

    this.setText("perLead", this.money(unit));
    this.setText("sumQuality", this.titleize(tier));
    this.setText("savingsAmt", this.money(savings));
    this.setText("subSaveAmt", this.money(savings));
    if (this.hasSavingsLineTarget) this.savingsLineTarget.style.display = isSub ? "" : "none";
    if (this.hasSecureSubTarget) this.secureSubTarget.style.display = isSub ? "" : "none";

    let total;
    let leadsShown;
    let label;
    let note;
    if (isSub) {
      // The first payment collects the weekly subscription AND the one-time
      // bump together, so the headline total must include both — showing only
      // the recurring amount understates what the buyer pays today.
      const bumpAmt = bumpOn ? Number(bump.total_price) : 0;
      const weeklyAmt = subPrice * quantity;
      total = weeklyAmt + bumpAmt;
      leadsShown = quantity + (bumpOn ? Number(bump.quantity) : 0);
      label = bumpOn ? "Due Today" : "Weekly Total";
      note = bumpOn
        ? `then ${this.money(weeklyAmt)} billed weekly · ${this.money(subPrice)} per lead`
        : `${this.money(subPrice)} per lead · billed weekly`;
      // #16037: a super admin's custom per-lead weekly price for this buyer.
      // The server already priced subscription_price from it; say so.
      if (row.custom_subscription_price) note = `Your special price · ${note}`;
    } else {
      const bumpAmt = bumpOn ? Number(bump.total_price) : 0;
      total = unit * quantity + bumpAmt;
      leadsShown = quantity + (bumpOn ? Number(bump.quantity) : 0);
      label = "Order Total";
      note = `${this.money(leadsShown > 0 ? total / leadsShown : 0)} per lead · one-time`;
    }

    // Bump line (shows the one-time add-on; annotated when on a weekly plan).
    if (this.hasBumpLineTarget) this.bumpLineTarget.style.display = bumpOn ? "" : "none";
    if (bumpOn) {
      this.setText("bumpLineK", `Bump Leads (${bump.quantity})${isSub ? " · one-time" : ""}`);
      this.setText("bumpLineV", `+ ${this.money(bump.total_price)}`);
    }

    this.setText("sumLeads", String(leadsShown));
    this.setText("totalLabel", label);
    const totalText = this.money(total);
    this.setText("totalVal", totalText);
    this.setText("perLeadNote", note);

    // Briefly highlight the total when it actually changes (reuses the shared
    // .price-highlight animation), so a recalculated total draws the eye.
    if (totalText !== this._lastTotalText) {
      if (this._lastTotalText !== undefined && this.hasTotalValTarget) {
        this.applyPriceHighlight(this.totalValTarget);
      }
      this._lastTotalText = totalText;
    }
  }

  applyPriceHighlight(element) {
    element.classList.remove("price-highlight");
    void element.offsetWidth; // force reflow so the animation restarts
    element.classList.add("price-highlight");
  }

  // Admin-only pricing warnings (e.g. a missing pricing package). The
  // server only sends these to admins, so the element stays empty for everyone
  // else. Shown once per page load to avoid churn on every refetch.
  showAdminPricingWarnings(warnings) {
    if (!Array.isArray(warnings) || warnings.length === 0) return;
    if (this._adminWarningsShown || !this.hasPricingWarningTarget) return;
    this._adminWarningsShown = true;
    this.pricingWarningTarget.textContent = warnings.join(" · ");
    this.pricingWarningTarget.style.display = "";
  }

  // Fires when any section's Completed checkbox changes. Enforces section-specific
  // rules (Order Options needs ≥1 state and ≥1 delivery day; a self-bought
  // Thank-You step needs a photo OR the deliberate "I'll add my photo later")
  // with inline guidance instead of a late failure at submit, then recomputes
  // the gate.
  onSectionCompleted(event) {
    const detail = event.detail || {};
    if (detail.completed) {
      const message = this.sectionValidationError(detail.key);
      if (message) {
        this.revertSectionCompletion(event.target);
        showAlertModal(message);
        return; // revertSectionCompletion re-dispatches -> recomputeGate runs then
      }
    }
    this.recomputeGate();
    if (detail.completed) {
      this.advanceAccordion(detail.key, true);
    } else {
      // Un-completing a step re-locks every step after it.
      this.syncStepLocks();
    }
  }

  sectionValidationError(key) {
    if (key === "options") return this.optionsValidationError();
    if (key === "thankyou") return this.thankYouValidationError();
    return null;
  }

  // Self-buy photo NUDGE (#12797; owner ruling 2026-09-14: strongly encouraged,
  // never required — the onboarding step-6 pattern, #12341). The Thank-You step
  // completes with a profile photo (the slot's preview carries the photoPreview
  // target only when one exists), a chosen file, OR the deliberate "I'll add my
  // photo later" click; without any of the three the buyer is pointed at the
  // two choices. Buying-for is warned server-side, never gated.
  thankYouValidationError() {
    if (this.buyingFor() || this.photoProvided() || this.photoSkipped === true) return null;
    return PHOTO_STEP_MESSAGE;
  }

  photoProvided() {
    return this.hasPhotoPreviewTarget || (this.photoInput()?.files?.length || 0) > 0;
  }

  photoInput() {
    return this.element.querySelector("#lead_order_photo");
  }

  optionsValidationError() {
    const hasStates = this.stateChipTargets.some((c) => c.classList.contains("is-on"));
    const hasDays = this.dayChipTargets.some((c) => c.classList.contains("is-on"));
    if (!hasStates) return "Select at least one target state before completing this section.";
    if (!hasDays) return "Select at least one delivery day before completing this section.";
    if (this.daysClearedToFloor && this.selectedChipValues(this.dayChipTargets, "day").length === 1) {
      return "Clear left only today selected. Pick the days leads should deliver, or use Copy From Profile.";
    }
    const capError = this.maxPerDayError();
    if (capError) return capError;
    // A Local order needs a ZIP selection the buyer has actually seen. The
    // server refuses an empty list either way (LeadOrders::GeoParams), but
    // without this the buyer only discovers it as a raw error at final submit,
    // after filling in the rest of the form.
    if (this.geoMode() === "radius" && this.selectedZips().length === 0) {
      return this.zipRowTargets.length === 0
        ? "Click Find ZIP Codes to choose the area for your local order."
        : "Select at least one ZIP code for your local order.";
    }
    return this.deliveryWindowError();
  }

  // The shared client mirror of LeadOrders::DeliveryWindowRule (lib/delivery_window).
  deliveryWindowError() {
    return deliveryWindowError(this.deliveryWindowValue("windowStart"), this.deliveryWindowValue("windowEnd"));
  }

  deliveryWindowValue(targetName) {
    const key = `has${targetName.charAt(0).toUpperCase()}${targetName.slice(1)}Target`;
    return timeInputValue(this[key] ? this[`${targetName}Target`] : null);
  }

  // Uncheck + re-expand a section by driving its own rlo-section controller, so the
  // badge/collapse state stays consistent (single source of truth for that logic).
  revertSectionCompletion(sectionEl) {
    const section = sectionEl?.closest?.(".rlo-section") || sectionEl;
    const controller = this.application.getControllerForElementAndIdentifier(section, "rlo-section");
    if (controller?.hasCompletedTarget) {
      controller.completedTarget.checked = false;
      controller.toggleCompleted();
    }
  }

  // Progress bar + checklist (5 steps) + Add to Cart gate: the 3 form sections,
  // a selected tier (which covers steps 1-2) and a trustworthy price.
  recomputeGate() {
    const sections = Array.from(this.element.querySelectorAll(".rlo-section"));
    const completedKeys = new Set(
      sections
        .filter((s) => s.classList.contains("is-completed"))
        .map((s) => s.dataset.rloSectionKey),
    );
    const total = sections.length;
    const done = completedKeys.size;

    // Steps 1-2 join the checklist + progress math as pseudo-keys.
    if (this.typePicked) completedKeys.add("lead_type");
    if (this.tierPicked) completedKeys.add("lead_quality");
    const stepsTotal = total + 2;
    const stepsDone = done + (this.typePicked ? 1 : 0) + (this.tierPicked ? 1 : 0);

    this.checkItemTargets.forEach((item) => {
      item.classList.toggle("is-done", completedKeys.has(item.dataset.section));
    });
    if (this.hasProgressLabelTarget) {
      this.progressLabelTarget.textContent = `${stepsDone} of ${stepsTotal} Required`;
    }
    if (this.hasProgressFillTarget) {
      this.progressFillTarget.style.width = stepsTotal ? `${(stepsDone / stepsTotal) * 100}%` : "0%";
    }

    // pricingValid guards against carting an order for which we never showed a
    // trustworthy price (e.g. a /pricing outage) — the buyer must see a real
    // total before Add to Cart enables.
    const ready = total > 0 && done === total && !!this.selectedTier() && this.pricingValid === true;
    this.setPrimaryDisabled(!ready);
    // The hint carries the Buy-switch reason while purchases are closed (#14937).
    if (this.hasCartHintTarget) this.cartHintTarget.style.display = ready && !this.purchaseBlocked() ? "none" : "";
  }

  // --- Slow-fill warning (#16834) ---
  // Owner, 2026-10-12: warn on the order page when the picked states will fill
  // the order slowly. Non-blocking; the server writes the sentence (days, the
  // goal and state codes — never counts). Debounced, and token-guarded like
  // pricing so a stale answer never paints over a newer selection.
  scheduleFillForecast() {
    if (!this.hasFillForecastTarget) return;
    // A changed selection is a new forecast: "Keep My Restrictions" answered the
    // old one, so the Yes/No question is asked again.
    this._restrictionsKept = false;
    clearTimeout(this._forecastTimer);
    this._forecastTimer = setTimeout(() => this.refreshFillForecast(), 400);
  }

  async refreshFillForecast() {
    if (this._closed || !this.hasFillForecastTarget) return;
    const token = (this._forecastToken = (this._forecastToken || 0) + 1);
    const quality = this.selectedTier();
    const states = this.selectedStates();
    if (!this.hasFillForecastUrlValue || !this.selectedClassValue || !quality || states.length === 0) {
      this.paintFillForecast(null);
      return;
    }
    const query = fillForecastQuery({
      leadClass: this.selectedClassValue,
      quality,
      quantity: this.clampedQty(),
      states,
      days: this.selectedChipValues(this.dayChipTargets, "day"),
      maxPerDay: this.hasMaxPerDayTarget ? String(this.maxPerDayTarget.value || "").trim() : "",
      windowStart: this.deliveryWindowValue("windowStart"),
      windowEnd: this.deliveryWindowValue("windowEnd"),
    });
    try {
      const data = await fetchFillForecast(this.fillForecastUrlValue, query);
      if (token !== this._forecastToken || this._closed) return;
      this.paintFillForecast(data);
    } catch {
      // Advisory only: a failed forecast hides the box rather than block the order.
      if (token === this._forecastToken) this.paintFillForecast(null);
    }
  }

  paintFillForecast(data) {
    this._lastForecast = data;
    this._restrictionsWarningShowing = Boolean(data?.warn && data?.restrictions_warning);
    renderFillForecast(this.fillForecastTarget, data, { restrictionsKept: this._restrictionsKept });
  }

  // "Would you like to remove all restrictions now?" — Yes: every day, no daily
  // max, any time of day (the same controls the agent would clear by hand).
  removeAllRestrictions() {
    this._restrictionsKept = false;
    this.daysSelectAll();
    if (this.hasMaxPerDayTarget) this.maxPerDayTarget.value = "";
    if (this.hasWindowStartTarget) this.windowStartTarget.value = "";
    if (this.hasWindowEndTarget) this.windowEndTarget.value = "";
    this.updateDailyCapSplitHint();
    this.updateLimitsWarning();
  }

  // No: keep them. The warning stays as the record of what was shown; the
  // order carries the choice when it is placed.
  keepRestrictions() {
    this._restrictionsKept = true;
    renderFillForecast(this.fillForecastTarget, this._lastForecast, { restrictionsKept: true });
  }

  // --- State Lead Flow (#15927) ---
  // Every state ranked by its 30-day share of the picked type + quality (shares
  // only, never counts — lib/state_lead_flow). A modal on the page; inside the
  // GOAT Store dialog, an in-section panel, since a dialog may carry no nested
  // modal (#13900 §6).

  // BUTTON-4: the disabled button carries its reason (tooltip + accessible
  // name), rendered by the view; withdraw it on enable and restore it on disable.
  syncStateLeadFlowButton() {
    if (!this.hasStateFlowButtonTarget) return;
    const button = this.stateFlowButtonTarget;
    // The ranking is for the picked type AND quality (#15944): a type whose
    // every tier is switched off (#10182) leaves it disabled, never clickable
    // and silent.
    const disabled = !(this.selectedClassValue && this.selectedTier());
    button.disabled = disabled;
    const reason = button.dataset.disabledReason;
    if (!reason) return;
    if (disabled) {
      button.setAttribute("data-gl-tooltip", reason);
      button.setAttribute("aria-label", `State Lead Flow — ${reason}`);
    } else {
      button.removeAttribute("data-gl-tooltip");
      button.removeAttribute("aria-label");
    }
  }

  // The tiers the agent can buy for the picked type, in page order.
  purchasableTiers() {
    const group = this.tiersGroupTargets.find((g) => g.classList.contains("is-active"));
    if (!group) return [];
    return Array.from(group.querySelectorAll(".rlo-tier:not(.is-unavailable)"))
      .map((tile) => tile.dataset.tier)
      .filter(Boolean);
  }

  openStateLeadFlow() {
    const qualities = this.purchasableTiers();
    const quality = this.selectedTier();
    if (!this.selectedClassValue || !quality || !this.hasStateLeadFlowUrlValue) return;

    this.closeStateLeadFlow();
    const options = {
      url: this.stateLeadFlowUrlValue,
      leadClass: this.selectedClassValue,
      quality,
      qualities,
      selectedStates: this.selectedStates(),
    };

    if (this.hostedValue) {
      if (!this.hasStateFlowPanelTarget || !this.hasStateFlowBodyTarget) return;
      this.stateFlowPanelTarget.hidden = false;
      this._stateFlow = mountStateLeadFlow(this.stateFlowBodyTarget, options);
      return;
    }

    this._stateFlowShell = mountGlModalShell({
      idPrefix: "rlo-state-flow",
      title: "State Lead Flow",
      headerIcon: "ph-fill ph-chart-bar",
      // 51 rows: the dialog stays within the viewport and its body scrolls.
      scrollable: true,
      // Inside the controller root so the page's .rlo-chip styles reach the toggle.
      container: this.element,
      buttons: [{ text: "Close", className: "gl-btn gl-btn-primary", dismiss: true }],
      onMounted: (shell) => {
        this._stateFlow = mountStateLeadFlow(shell.body, options);
      },
      // A superseded shell (reopened while it was still fading out) must not
      // tear down the flow and shell that replaced it.
      onHidden: (shell) => {
        if (this._stateFlowShell !== shell) return;
        this._stateFlow?.destroy();
        this._stateFlow = null;
        this._stateFlowShell = null;
      },
    });
  }

  closeStateLeadFlow({ destroy = false } = {}) {
    this._stateFlow?.destroy();
    this._stateFlow = null;
    if (destroy) {
      this._stateFlowShell?.destroy();
      this._stateFlowShell = null;
    } else {
      this._stateFlowShell?.hide();
    }
    if (this.hasStateFlowPanelTarget) this.stateFlowPanelTarget.hidden = true;
  }

  // --- "View Data Fields" panel (per tier) ---
  // An in-section panel under the tiers, filled from load_fields_card — the same
  // server-rendered fields card in both shapes. It replaced a bespoke .rlo-modal
  // overlay: the GOAT Store dialog may carry no nested modal (#13900 §6), and
  // the page has no reason to differ.

  async openFieldsPanel(event) {
    event.stopPropagation(); // don't also toggle tier selection
    const button = event.currentTarget;
    const leadClass = button.dataset.leadClassUnderscore;
    const tier = button.dataset.tier;
    if (!leadClass || !tier || !this.hasLoadFieldsUrlValue || !this.hasFieldsPanelTarget) return;

    // Monotonic token: two quick tile clicks, a close, or disconnect() drop the
    // response that is no longer wanted instead of painting it.
    const token = (this._fieldsToken = (this._fieldsToken || 0) + 1);
    if (this.hasFieldsTitleTarget) {
      this.fieldsTitleTarget.textContent = `${button.dataset.tierName || this.titleize(tier)} — Data Fields`;
    }
    this.fieldsPanelTarget.hidden = false;
    if (this.hasFieldsBodyTarget) this.fieldsBodyTarget.innerHTML = '<p class="rlo-fields-panel__loading">Loading…</p>';
    let html;
    try {
      const response = await get(this.loadFieldsUrlValue, {
        query: { lead_class: leadClass, partial: `${leadClass}_${tier}_fields_card` },
        responseKind: "html",
      });
      if (token !== this._fieldsToken) return;
      // Trusted, server-rendered partial (our own fields card).
      html = response.ok ? await response.html : "<p>Data fields are unavailable right now.</p>";
    } catch {
      html = "<p>Data fields are unavailable right now.</p>";
    }
    if (token !== this._fieldsToken) return;
    if (this.hasFieldsBodyTarget) this.fieldsBodyTarget.innerHTML = html;
  }

  closeFieldsPanel() {
    this._fieldsToken = (this._fieldsToken || 0) + 1; // a load still in flight is no longer wanted
    if (this.hasFieldsPanelTarget) this.fieldsPanelTarget.hidden = true;
  }

  // Gather the order and POST it to /create. The server re-computes + snapshots
  // the charged amount and runs every existing order gate; on success it carts.
  async submitOrder() {
    if (this.primaryDisabled() || this._submitting) return;
    if (!this.hasCreateUrlValue) return;

    // Disable immediately — the Trucker acknowledgement modal below is an `await`, and a
    // second click while it's open would otherwise re-enter and double-submit (#10586
    // low #7). Re-enabled on cancel or on error. _submitting covers the rail and
    // the dialog-footer primaries together: one order per click, whichever was clicked.
    this._submitting = true;
    this.setPrimaryDisabled(true);

    // Unlicensed-state check runs FIRST: it is the only prompt here that asks
    // the buyer to go back and change the order, so raising it before the
    // Trucker acknowledgement avoids making them accept a delivery caveat for
    // an order they are about to fix.
    const statesOk = await confirmStatesIfUnlicensed(this.selectedStates(), {
      ...this.unlicensedStatesContext,
      actionLabel: this.buyingFor() ? "continue" : "add it to the cart",
    });
    if (!statesOk) {
      this.reenablePrimary();
      return;
    }

    // #13517: the second "go back and fix" prompt — a one- or two-day schedule —
    // still ahead of the Trucker caveat, for the same reason as the states check.
    const daysOk = await this.confirmFewDeliveryDays();
    if (!daysOk) {
      this.reenablePrimary();
      return;
    }

    // Trucker orders take longer to fill — acknowledge before carting, and offer
    // the faster/cheaper Real-Time IUL leads as an alternative.
    if (this.selectedClassValue === this.truckerClassValue) {
      const iulLink = this.hasIulUrlValue
        ? ` Prefer faster delivery? <a href="${this.iulUrlValue}">Real-Time IUL leads</a> fill quicker and cost less.`
        : "";
      const ok = await showAcknowledgementModal({
        title: "Heads up: Trucker delivery time",
        bodyHTML: `<p>Real-Time Trucker orders can take <strong>7–10 days</strong> to fill.${iulLink}</p>`,
        checkboxLabel: "I understand Trucker orders can take 7–10 days to fill.",
        confirmText: this.buyingFor() ? "Continue" : "Add to Cart",
        cancelText: "Cancel",
        variant: "warning",
      });
      if (!ok) {
        this.reenablePrimary();
        return;
      }
    }
    // The confirmations above are awaits: the dialog may have been closed
    // (disconnected) while one was open. Then nothing is posted.
    if (this._closed) {
      this._submitting = false;
      return;
    }

    this.hideCartError();
    this.setPrimaryBusy(true);

    try {
      const response = await post(this.createUrlValue, { body: this.buildOrderFormData() });
      // A session that ended mid-order: the POST followed the sign-in 302 to a
      // 200 HTML page (no JSON, no redirect_url), and navigateTo('/cart') then
      // bounced through sign-in to an EMPTY cart. Log in first, with return_to
      // on this page (#14273 item 7). A 401 (JSON shape) is the same case.
      if (sessionExpiredResponse(response)) {
        if (!this._closed) redirectToLoginForExpiredSession();
        return;
      }
      if (!response.ok) {
        throw new Error(await this.extractError(response));
      }
      // Every order is carted, an Agency Lead Order included (#19074): the
      // server answers this XHR with { redirect_url } — the cart, plus any tip
      // param (RealtimeLeadOrdersController#navigate_client_to).
      const redirectUrl = await this.extractRedirectUrl(response);
      // A response landing after the screen was torn down (dialog closed during
      // the POST) must not navigate a later instance's page (review finding 5).
      // The order is carted server-side; the cart shows it on the next visit.
      if (this._closed) return;
      this.navigateTo(redirectUrl || (this.hasCartUrlValue ? this.cartUrlValue : "/cart"));
    } catch (error) {
      if (this._closed) return;
      this.setPrimaryBusy(false);
      this.reenablePrimary();
      this.showCartError(error?.message || "Unable to process this order. Please try again.");
    }
  }

  reenablePrimary() {
    this._submitting = false;
    this.setPrimaryDisabled(false);
  }

  buildOrderFormData() {
    const fd = new FormData();
    const append = (key, value) => fd.append(key, value ?? "");

    append("lead_order[lead_class]", this.selectedClassValue);
    const tier = this.selectedTier();
    if (tier) append("lead_order[lead_quality]", tier);
    append("lead_order[total_lead_order]", String(this.clampedQty()));
    append("lead_order[max_per_day]", this.hasMaxPerDayTarget ? this.maxPerDayTarget.value : "");
    append("lead_order[delivery_window_start]", this.deliveryWindowValue("windowStart"));
    append("lead_order[delivery_window_end]", this.deliveryWindowValue("windowEnd"));
    append("lead_order[frequency]", this.isSubscription() ? "weekly" : "one_time");

    // Agency Lead Order (#19074): top-level agency_agent_id (the server
    // re-resolves it through the owner's own active links). The agent's
    // thank-you details and integrations are applied when the order is paid.
    if (this.buyingFor()) fd.append("agency_agent_id", this.agencyAgentIdValue);

    // Thank-you display fields (ids match the create endpoint's permitted names).
    ["display_agent_name", "display_agent_title", "agent_phone", "agent_email",
      "display_npn", "display_agent_website", "display_calendar_link"].forEach((field) => {
      append(`lead_order[${field}]`, this.fieldValue(`lead_order_${field}`));
    });

    // Notification preferences — send explicit 1/0 so an unchecked box overrides
    // the profile default rather than silently falling back to it.
    ["notify_agent_email", "notify_agent_text", "notify_agent_in_app",
      "notify_agent_include_pdf", "notify_lead_email", "notify_lead_text"].forEach((field) => {
      append(`lead_order[${field}]`, this.namedChecked(`lead_order[${field}]`) ? "1" : "0");
    });

    // Integrations — omitted entirely when buying-for (#9485): the page renders
    // no owner integration inputs, and absent params (vs explicit blanks) let
    // the AGENT's own profile callbacks (webhook / GHL) apply at create.
    if (!this.buyingFor()) {
      append("lead_order[ringy_integration_id]", this.namedValue("ringy_integration_id"));
      append("lead_order[google_sheet_integration_id]", this.namedValue("google_sheet_integration_id"));
      append("lead_order[webhook_url]", this.namedValue("webhook_url"));
      append("lead_order[ghl_enabled]", this.namedChecked("ghl_enabled") ? "1" : "0");
      this.selectedGhlTags().forEach((tag) => fd.append("lead_order[ghl_tags][]", tag));
    }

    this.selectedChipValues(this.stateChipTargets, "state").forEach((s) =>
      fd.append("lead_order[states][]", s));
    // Local targeting. Sent as a REQUEST only — LeadOrders::GeoParams re-resolves
    // the radius server-side and keeps just the intersection.
    append("lead_order[geo_mode]", this.geoMode());
    if (this.geoMode() === "radius") {
      append("lead_order[geo_center_zip]", (this.geoCenterZipTarget.value || "").trim());
      append("lead_order[geo_radius_miles]", String(this.geoRadiusTarget.value));
      this.selectedZips().forEach((z) => fd.append("lead_order[geo_zips][]", z));
    }
    this.selectedChipValues(this.dayChipTargets, "day").forEach((d) =>
      fd.append("lead_order[days_per_week][]", d));

    const photo = this.photoInput()?.files?.[0];
    if (photo) fd.append("lead_order[photo]", photo);
    // "Also use this as my Display Photo" (#12797, #12911) — a top-level flag like
    // save_thank_you_defaults: sent only WITH a file, when the box is rendered
    // and checked, never on a buy-for (the server mirrors all three, and never
    // replaces an existing Display Photo).
    if (photo && !this.buyingFor() && this.saveProfilePhotoRequested()) fd.append("save_profile_photo", "1");
    // Still posted, but create does not decide on it for this page's orders:
    // a self-buy with a Display Photo always gets it copied when no file was
    // uploaded, and an Agency Lead Order never copies the payer's
    // (RealtimeLeadOrdersController#copy_profile_photo?).
    append(
      "lead_order[use_profile_photo]",
      this.hasUseProfilePhotoFlagTarget ? this.useProfilePhotoFlagTarget.value : "0",
    );

    // Switch Subscription (#13430) — top-level like agency_agent_id. The old
    // subscription is not touched here: it is canceled only once THIS order's
    // first payment succeeds (LeadOrders::SubscriptionSwitch), so abandoning
    // checkout leaves the agent on the subscription they already had.
    if (this.hasSwitchFromIdValue && this.switchFromIdValue !== "") {
      fd.append("switch_from_lead_order_id", this.switchFromIdValue);
    }

    // An Agency Lead Order never bumps (#19074).
    if (!this.buyingFor() && this.bumpSelected()) {
      fd.append("bump_order", "1");
      // One checkout, one daily cap (#14067): the server divides the typed cap
      // between the main order and the bump (LeadOrders::DailyCapSplit, after
      // both are carted) when this box is left checked.
      if (this.dailyCapSplitRequested()) fd.append("apply_daily_cap_to_transaction", "1");
    }

    // "Save these as my profile defaults" (#12306) — a top-level flag like
    // bump_order, sent only when the box is rendered AND checked (the server
    // reads params[:save_thank_you_defaults] == "1"; never on a buy-for).
    if (!this.buyingFor() && this.saveDefaultsRequested()) fd.append("save_thank_you_defaults", "1");
    // #16834 (owner 2026-10-13): the order is placed while the slow-states +
    // restrictions warning shows, so the agent kept their restrictions despite
    // it — the server records that on the order (LeadOrders::RestrictionsWarningKept).
    if (this._restrictionsWarningShowing) fd.append("slow_fill_restrictions_kept", "1");
    this.appendUnlistedInputs(fd);
    return fd;
  }

  // Names the hand-built section above owns — including the ones it appends
  // only conditionally (buying-for omits the integrations), a name no screen
  // may post (thank_you_source) and the legacy bare names it re-keys under
  // lead_order[...]. appendUnlistedInputs never touches these, so the explicit
  // 1/0 and omit-when-buying-for semantics stay exactly as written.
  static HAND_BUILT_NAMES = new Set([
    "lead_order[thank_you_source]", "lead_order[photo]",
    // Top-level flags the builder decides for itself (rendered checkbox or not):
    // bump_order is gated on the visible bump box; save_thank_you_defaults and
    // save_profile_photo are never sent on a buy-for (the latter only with a
    // file), agency_agent_id never on a self-buy.
    "save_thank_you_defaults", "save_profile_photo", "bump_order", "apply_daily_cap_to_transaction", "agency_agent_id",
    "slow_fill_restrictions_kept",
    // Step 5's GOAT CRM / My Own CRM cards (#17716) only switch which panel
    // shows; the choice itself is never part of the order.
    "crm_destination_choice",
    "ringy_integration_id", "google_sheet_integration_id", "webhook_url", "ghl_enabled", "ghl_tags[]",
    ...["display_agent_name", "display_agent_title", "agent_phone", "agent_email",
      "display_npn", "display_agent_website", "display_calendar_link",
      "notify_agent_email", "notify_agent_text", "notify_agent_in_app",
      "notify_agent_include_pdf", "notify_lead_email", "notify_lead_text",
      "use_profile_photo"].map((f) => `lead_order[${f}]`),
  ]);

  static SWEPT_INPUT_TYPES_SKIPPED = new Set(["file", "submit", "button", "reset", "image"]);

  // The page has no <form>: this builder IS the submit. Every [name] control the
  // sections render that the hand-built part above does not know about is
  // carried here with native form semantics (checked box → its value, unchecked
  // → omitted; radio → the checked one; multi-select → each selected option;
  // everything else → value). Without this, a new input silently never reached
  // the server while request specs posting the param directly stayed green —
  // twice in one day (#12332's save checkbox, #12337's GHL flag; #12340).
  appendUnlistedInputs(fd) {
    const groups = new Map();
    this.sweptControls().forEach((el) => {
      const { name } = el;
      if (!name || el.disabled) return;
      if (this.constructor.HAND_BUILT_NAMES.has(name) || fd.has(name)) return;
      if (this.constructor.SWEPT_INPUT_TYPES_SKIPPED.has(el.type)) return;
      if (!groups.has(name)) groups.set(name, []);
      groups.get(name).push(el);
    });
    groups.forEach((controls, name) => this.appendNamedGroup(fd, name, controls));
  }

  // One name, every control that carries it, native semantics. Rails'
  // check_box / collection_check_boxes render a hidden fallback BEFORE the
  // box(es); a first-wins gate would post the fallback and drop the checked
  // value (#12350 review). So: checked boxes/radios win; with none checked the
  // hidden fallback posts (the unchecked value); a name with no checkable
  // control posts each control's value (multi-selects every selected option).
  appendNamedGroup(fd, name, controls) {
    const checkable = controls.filter((el) => el.type === "checkbox" || el.type === "radio");
    if (checkable.length) {
      const checked = checkable.filter((el) => el.checked);
      if (checked.length) {
        checked.forEach((el) => fd.append(name, el.value));
        return;
      }
      const fallback = controls.find((el) => el.type === "hidden");
      if (fallback) fd.append(name, fallback.value ?? "");
      return;
    }
    controls.forEach((el) => {
      if (el.tagName === "SELECT" && el.multiple) {
        [...el.selectedOptions].forEach((option) => fd.append(name, option.value));
      } else {
        fd.append(name, el.value ?? "");
      }
    });
  }

  // The build column and the summary rail are the order; the shell chrome
  // around them (nav, search) is not. Fixtures without those containers are
  // swept whole.
  sweptControls() {
    const scoped = this.element.querySelectorAll(
      ".rlo-build input[name], .rlo-build select[name], .rlo-build textarea[name], " +
      ".rlo-rail input[name], .rlo-rail select[name], .rlo-rail textarea[name]",
    );
    if (scoped.length || this.element.querySelector(".rlo-build, .rlo-rail")) return [...scoped];
    return [...this.element.querySelectorAll("input[name], select[name], textarea[name]")];
  }

  // --- submit helpers ---

  saveDefaultsRequested() {
    return this.hasSaveDefaultsBoxTarget && this.saveDefaultsBoxTarget.checked;
  }

  saveProfilePhotoRequested() {
    return this.hasSaveProfilePhotoBoxTarget && this.saveProfilePhotoBoxTarget.checked;
  }

  isSubscription() {
    return this.hasSubToggleTarget && this.subToggleTarget.checked;
  }

  bumpSelected() {
    return (
      this.hasBumpCheckboxTarget &&
      this.bumpCheckboxTarget.checked &&
      this.hasBumpBoxTarget &&
      this.bumpBoxTarget.style.display !== "none"
    );
  }

  // Scoped to this screen's root: hosted over another page, the dialog must
  // post ITS fields, never the background page's (review finding 5).
  fieldValue(id) {
    return this.element.querySelector(`#${id}`)?.value?.trim() || "";
  }

  namedValue(name) {
    return this.element.querySelector(`[name="${name}"]`)?.value || "";
  }

  // Only checkable controls count: a hidden input sharing the name must never
  // shadow the checkbox (that shadowing posted ghl_enabled=0 for a "syncing"
  // GHL row on #12337 — the case the payload-contract Jest pins).
  namedChecked(name) {
    return !!this.element.querySelector(
      `input[type="checkbox"][name="${name}"], input[type="radio"][name="${name}"]:checked`,
    )?.checked;
  }

  selectedChipValues(chips, dataKey) {
    return chips
      .filter((chip) => chip.classList.contains("is-on"))
      .map((chip) => chip.dataset[dataKey]);
  }

  selectedGhlTags() {
    const select = this.element.querySelector('select[name="ghl_tags[]"]');
    if (!select) return [];
    return Array.from(select.selectedOptions).map((option) => option.value);
  }

  // Two error shapes: `{ error: { message } }` from the order gates, and the
  // CSRF handler's `{ error: "Form Auth Token expired", authenticity_token }`
  // (ApplicationController#handle_csrf_error) — a STRING, shown as is (read as
  // `error.message` it would become the generic sentence), and its fresh token
  // is adopted so the retry does not fail the same way (#14273 item 7). The
  // token goes into the page's meta tag, which request.js reads on every call.
  async extractError(response) {
    try {
      const data = await response.json;
      this.adoptAuthenticityToken(data?.authenticity_token);
      const error = data?.error;
      if (typeof error === "string" && error) return error;
      return error?.message || "Unable to process this order.";
    } catch {
      return "Unable to process this order.";
    }
  }

  adoptAuthenticityToken(token) {
    if (typeof token !== "string" || !token) return;
    const meta = document.querySelector('meta[name="csrf-token"]');
    if (meta) meta.content = token;
  }

  // A JSON success body carries the next URL (create's XHR answer — the cart);
  // anything that is not JSON resolves to null, and the caller falls back to
  // the cart URL.
  async extractRedirectUrl(response) {
    if (!String(response.contentType || "").includes("json")) return null;
    try {
      const data = await response.json;
      return data?.redirect_url || null;
    } catch {
      return null;
    }
  }

  showCartError(message) {
    if (!this.hasCartErrorTarget) return showAlertModal(message);
    this.cartErrorTarget.textContent = message;
    this.cartErrorTarget.style.display = "";
    return undefined;
  }

  hideCartError() {
    if (this.hasCartErrorTarget) this.cartErrorTarget.style.display = "none";
  }

  // --- Helpers ---

  setText(targetName, text) {
    const key = `has${targetName.charAt(0).toUpperCase()}${targetName.slice(1)}Target`;
    if (this[key]) this[`${targetName}Target`].textContent = text;
  }

  // setText, but a blank value renders the muted empty-value marker.
  setTextOrEmpty(targetName, text) {
    const key = `has${targetName.charAt(0).toUpperCase()}${targetName.slice(1)}Target`;
    if (this[key]) setValueOrEmpty(this[`${targetName}Target`], text);
  }

  money(amount) {
    // Guard a malformed/undefined value (e.g. a bad bump_offer field) against rendering
    // "$NaN" on the page (#10586 low #7).
    const value = Number(amount) || 0;
    return `$${value.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  }

  titleize(value) {
    return value ? value.charAt(0).toUpperCase() + value.slice(1) : "";
  }
}
